import React from "react";

const eyebrowStyle = (value) => {
  const txt = String(value || "").toLowerCase();
  if (txt.includes("kennzahlen")) {
    return {
      color: "#ffffff",
      WebkitTextFillColor: "#ffffff",
      textShadow: "none"
    };
  }
  return undefined;
};
import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink, FileText, Eye, ChevronRight, ChevronDown, Image as ImageIcon, Play } from "lucide-react";
import { ASSET_URL, api } from "../../lib/api";
import { useSite } from "../../lib/site-context";
import { useParallax } from "../../lib/motion";
import PdfViewer from "../PdfViewer";

const pick = (sec, lang) => sec.content?.[lang] || sec.content?.de || {};

// CTA that smooth-scrolls to an in-page anchor (#id) or routes normally otherwise.
function CTA({ to, className, children, testid }) {
  const target = to || "";
  if (target.startsWith("#")) {
    return (
      <a
        href={target}
        data-testid={testid}
        className={className}
        onClick={(e) => {
          const el = document.getElementById(target.slice(1));
          if (el) { e.preventDefault(); el.scrollIntoView({ behavior: "smooth", block: "start" }); }
        }}
      >
        {children}
      </a>
    );
  }
  return <Link to={target || "#"} data-testid={testid} className={className}>{children}</Link>;
}

// Long section headlines read calmer as a "lead" style (wraps cleanly on mobile,
// less shouty) instead of the big display heading. Short ones stay as brut-h2.
const headlineClass = (text) => ((text || "").length > 42 ? "brut-headline-lead" : "brut-h2");

// Hero: bilingual media (uses media_id_en if available and lang=en)
export function Hero({ sec, media }) {
  const { lang, t } = useSite();
  const c = pick(sec, lang);
  // Use language-specific media when available, otherwise fall back to whichever slot is filled
  const effMediaId = (lang === "en" && sec.media_id_en) ? sec.media_id_en : (sec.media_id || sec.media_id_en);
  const effMediaMode = (lang === "en" && sec.media_mode_en) ? sec.media_mode_en : (sec.media_mode || sec.media_mode_en);
  const m = media?.[effMediaId];
  const mediaMode = effMediaMode || (m ? m.media_type : null);
  // Background media opacity (0–100 %) configurable per section; sensible defaults per type
  const opacityPct = (typeof sec.media_opacity === "number")
    ? sec.media_opacity
    : (mediaMode === "html_animation" ? 70 : 25);
  const mediaOpacity = Math.max(0, Math.min(100, opacityPct)) / 100;
  const circleRef = React.useRef(null);
  useParallax(circleRef, -0.25);
  const [mouseT, setMouseT] = React.useState({ x: 0, y: 0 });
  const heroRef = React.useRef(null);
  const heroVideoRef = React.useRef(null);

  // Keep the decorative hero loop alive after mobile tab/app switches.
  // Muting is also set as a DOM property because older Safari versions are
  // stricter than Chromium when deciding whether autoplay is allowed.
  const playHeroVideo = React.useCallback(() => {
    const video = heroVideoRef.current;
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    const promise = video.play();
    if (promise && typeof promise.catch === "function") promise.catch(() => {});
  }, []);

  React.useEffect(() => {
    if (mediaMode !== "video") return undefined;
    const resume = () => {
      if (document.visibilityState === "visible") playHeroVideo();
    };
    window.addEventListener("pageshow", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      window.removeEventListener("pageshow", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [mediaMode, playHeroVideo]);

  const onMouseMove = (e) => {
    const r = heroRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = ((e.clientX - r.left) / r.width - 0.5) * 20;
    const y = ((e.clientY - r.top) / r.height - 0.5) * 20;
    setMouseT({ x, y });
  };

  return (
    <section
      ref={heroRef}
      onMouseMove={onMouseMove}
      data-testid={`section-hero-${sec.id}`}
      className={`relative bg-[var(--bg)] overflow-hidden ${m && mediaMode ? "hero-with-media" : ""}`}
      style={sec.height_vh ? { minHeight: `${sec.height_vh}vh` } : undefined}
    >
      {/* Background media */}
      {m && mediaMode === "image" && (
        <img
          src={ASSET_URL(m.variants?.webp || m.variants?.original || "")}
          alt={lang === "en" ? m.alt_en || "" : m.alt_de || ""}
          className={`absolute inset-0 w-full h-full object-cover ${sec.media_bw === false ? "" : "brut-bw"}`}
          style={{ opacity: mediaOpacity }}
          loading="eager"
        />
      )}
      {m && mediaMode === "video" && (
        <video
          ref={heroVideoRef}
          className={`bg-video absolute inset-0 w-full h-full object-cover ${sec.media_bw === false ? "" : "brut-bw"}`}
          style={{ opacity: mediaOpacity }}
          src={ASSET_URL(m.variants?.original || "")}
          poster={m.variants?.poster ? ASSET_URL(m.variants.poster) : undefined}
          autoPlay
          muted
          defaultMuted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          onCanPlay={playHeroVideo}
        />
      )}
      {m && mediaMode === "html_animation" && (
        <iframe
          title={m.title || "Animation"}
          src={ASSET_URL(m.variants?.original || "")}
          className="absolute inset-0 w-full h-full pointer-events-none border-0"
          style={{ opacity: mediaOpacity, mixBlendMode: "multiply" }}
        />
      )}

      <div className="relative max-w-[1440px] mx-auto px-5 md:px-10 pt-36 md:pt-48 pb-20 md:pb-32">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 relative z-10">
            {c.eyebrow && (
              <div className="brut-tag mb-8 brut-reveal" data-testid="hero-eyebrow"><span>{c.eyebrow}</span></div>
            )}
            <h1 className="brut-h1 brut-reveal brut-reveal-delay-1" data-testid="hero-headline">
              {(c.headline || "").split("\n").map((l, i, arr) => (
                <span key={i} className="block">
                  {i === arr.length - 1 ? <span className="highlight-yellow is-visible">{l}</span> : l}
                </span>
              ))}
            </h1>
            {c.sub && (
              <p className="brut-body max-w-2xl mt-8 brut-reveal brut-reveal-delay-2" data-testid="hero-sub">
                {c.sub}
              </p>
            )}
            <div className="mt-10 flex flex-col sm:flex-row gap-4 brut-reveal brut-reveal-delay-3">
              {c.primary_cta_label && (
                <CTA to={c.primary_cta_url} className="brut-btn" testid="hero-cta-primary">
                  {c.primary_cta_label} <ArrowUpRight size={16} />
                </CTA>
              )}
              {c.secondary_cta_label && (
                <CTA to={c.secondary_cta_url} className="brut-btn brut-btn-ghost" testid="hero-cta-secondary">
                  {c.secondary_cta_label}
                </CTA>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function TextBlock({ sec }) {
  const { lang } = useSite();
  const c = pick(sec, lang);
  const padStyle = (typeof sec.pad_y === "number") ? { paddingTop: sec.pad_y, paddingBottom: sec.pad_y } : undefined;
  return (
    <section className="brut-section bg-[var(--bg)]" data-testid={`section-text-${sec.id}`}>
      <div className={`max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-12 gap-6 ${padStyle ? "" : "py-20 md:py-28"}`} style={padStyle}>
        <div className="col-span-12 md:col-span-3 min-w-0" data-reveal>
          {c.eyebrow && <div className="brut-tag" style={eyebrowStyle(c.eyebrow)}>{c.eyebrow}</div>}
        </div>
        <div className="col-span-12 md:col-span-9 min-w-0" data-reveal data-reveal-delay="1">
          <h2 className={`${headlineClass(c.headline)} mb-6`}>{(c.headline || "").split("\n").map((l,i)=><span key={i} className="block">{l}</span>)}</h2>
          <div className="legal-richtext max-w-4xl">
            {(c.body || "").split("\n\n").filter(Boolean).map((p, i) => (
              <p key={i} className="brut-body mt-4 whitespace-pre-line">{p}</p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function TextImage({ sec, media }) {
  const { lang } = useSite();
  const c = pick(sec, lang);
  // Use language-specific media when available, otherwise fall back to whichever slot is filled
  const effMediaId = (lang === "en" && sec.media_id_en) ? sec.media_id_en : (sec.media_id || sec.media_id_en);
  const m = media?.[effMediaId];
  const hasImage = !!(m && (m.variants?.webp || m.variants?.original));
  const right = c.image_side !== "left";
  const padStyle = (typeof sec.pad_y === "number") ? { paddingTop: sec.pad_y, paddingBottom: sec.pad_y } : undefined;
  const isHomeIntro = sec.id === "05d51d34-b2dd-407f-ae57-f4c4aecaea83" || sec.id === "fb8e1b6b-3ef5-41ba-828a-8783d417b125";
  return (
    <section className={`brut-section bg-[var(--bg)] ${isHomeIntro ? "home-intro-cleanflow" : ""}`} data-testid={`section-textimage-${sec.id}`}>
      <div className={`max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-12 gap-8 ${padStyle ? "" : "py-20 md:py-28"}`} style={padStyle}>
        <div className={`col-span-12 min-w-0 max-w-full overflow-hidden ${hasImage ? "md:col-span-6" : "md:col-span-8"} ${right ? "md:order-1" : "md:order-2"}`} data-reveal>
          {c.eyebrow && <div className="brut-tag mb-6" style={eyebrowStyle(c.eyebrow)}>{c.eyebrow}</div>}
          <h2 className={`${headlineClass(c.headline)} mb-6 ${isHomeIntro ? "hidden md:block" : ""}`}>{(c.headline || "").split("\n").map((l,i)=><span key={i} className="block">{l}</span>)}</h2>
          {isHomeIntro ? (
            <p className="font-sans w-full max-w-full text-left text-[16px] leading-[1.52] tracking-normal whitespace-pre-line break-words [overflow-wrap:anywhere] hyphens-auto text-[var(--ink)]">
              {c.body}
            </p>
          ) : (
            <p className="brut-body whitespace-pre-line max-w-2xl">{c.body}</p>
          )}
          {c.cta_label && (
            <Link to={c.cta_url || "#"} className="brut-btn mt-8" data-testid="textimage-cta">
              {c.cta_label} <ArrowUpRight size={16} />
            </Link>
          )}
        </div>
        {hasImage && (
          <div className={`col-span-12 md:col-span-6 min-w-0 ${right ? "md:order-2" : "md:order-1"}`} data-reveal data-reveal-delay="2">
            <div className="aspect-[4/5] md:aspect-[3/4] border-2 border-[var(--ink)] overflow-hidden brut-shadow lift-on-hover">
              <img
                src={ASSET_URL(m.variants?.webp || m.variants?.original || "")}
                alt={lang === "en" ? m.alt_en : m.alt_de}
                className={`w-full h-full object-cover ${sec.media_bw === false ? "" : "brut-bw"}`}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export function WorkflowSteps({ sec, media = {} }) {
  const { lang, t } = useSite();
  const c = pick(sec, lang);
  const steps = c.steps || [];
  const iconUrl = (s) => {
    const icon = s.icon_media_id ? media?.[s.icon_media_id] : null;
    return icon ? ASSET_URL(icon.variants?.webp || icon.variants?.original || "") : null;
  };

  return (
    <section className="brut-section bg-[var(--bg)]" data-testid={`section-workflow-${sec.id}`}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-3">{c.eyebrow && <div className="brut-tag" style={eyebrowStyle(c.eyebrow)}>{c.eyebrow}</div>}</div>
          <div className="col-span-12 md:col-span-9">
            <h2 className="brut-h2">{c.headline}</h2>
          </div>
        </div>

        {/* DESKTOP: horizontal flow with dotted connectors + arrows */}
        <ol className="hidden lg:flex items-start" data-testid="workflow-flow-desktop">
          {steps.map((s, i) => {
            const url = iconUrl(s);
            return (
              <React.Fragment key={i}>
                <li className="w-[130px] xl:w-[160px] shrink-0 flex flex-col items-center text-center" data-reveal data-reveal-delay={String((i % 4) + 1)}>
                  <div className="w-24 h-24 xl:w-28 xl:h-28 flex items-center justify-center">
                    {url
                      ? <img src={url} alt={s.title} className="w-full h-full object-contain" loading="lazy" data-testid={`workflow-icon-${s.no}`} />
                      : <span className="font-sans text-5xl font-semibold">{s.no}</span>}
                  </div>
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase opacity-50 mt-5 mb-1.5">{t("Schritt", "Step")} {s.no}</span>
                  <h3 className="font-cond font-black uppercase tracking-tight text-lg leading-tight">{s.title}</h3>
                  <p className="brut-body text-sm opacity-75 mt-1.5">{s.text}</p>
                </li>
                {i < steps.length - 1 && (
                  <li aria-hidden="true" className="flex-1 flex items-center gap-1 mt-[44px] xl:mt-[52px] px-1">
                    <span className="flex-1 border-t-2 border-dashed border-[var(--ink)]/35" />
                    <ChevronRight size={22} className="shrink-0 text-[var(--ink)]/55 -ml-1" strokeWidth={2.5} />
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ol>

        {/* MOBILE / TABLET: vertical timeline with dotted connectors + arrows */}
        <ol className="lg:hidden max-w-md mx-auto" data-testid="workflow-flow-mobile">
          {steps.map((s, i) => {
            const url = iconUrl(s);
            return (
              <React.Fragment key={i}>
                <li className="flex items-start gap-5" data-reveal>
                  <div className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center">
                    {url
                      ? <img src={url} alt={s.title} className="w-full h-full object-contain" loading="lazy" data-testid={`workflow-icon-${s.no}`} />
                      : <span className="font-sans text-4xl font-semibold">{s.no}</span>}
                  </div>
                  <div className="pt-1.5 min-w-0">
                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase opacity-50">{t("Schritt", "Step")} {s.no}</span>
                    <h3 className="font-cond font-black uppercase tracking-tight text-xl leading-tight mt-1">{s.title}</h3>
                    <p className="brut-body text-sm opacity-75 mt-1.5">{s.text}</p>
                  </div>
                </li>
                {i < steps.length - 1 && (
                  <li aria-hidden="true" className="flex flex-col items-center pl-10 sm:pl-12 -my-1">
                    <span className="border-l-2 border-dashed border-[var(--ink)]/35 h-8" />
                    <ChevronDown size={20} className="text-[var(--ink)]/55 -mt-1" strokeWidth={2.5} />
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ol>

        {c.cta_label && (
          <div className="mt-14 md:mt-16 flex justify-center lg:justify-start"><Link to={c.cta_url || "#"} className="brut-btn">{c.cta_label} <ArrowUpRight size={16}/></Link></div>
        )}
      </div>
    </section>
  );
}

export function Stats({ sec }) {
  const { lang } = useSite();
  const c = pick(sec, lang);
  return (
    <section className="brut-section bg-[var(--ink)] text-[var(--bg)]" data-testid={`section-stats-${sec.id}`}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 md:col-span-3">{c.eyebrow && <div className="brut-tag invert" style={eyebrowStyle(c.eyebrow)}>{c.eyebrow}</div>}</div>
          <div className="col-span-12 md:col-span-9"><h2 className="brut-h2">{c.headline}</h2></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-white/15">
          {(c.items || []).map((it, i, arr) => (
            <div
              key={i}
              className={`py-8 md:py-10 px-4 md:px-6 text-center md:text-left ${i !== arr.length - 1 ? "md:border-r border-white/15" : ""} ${i % 2 === 0 ? "border-r border-white/15 md:border-r" : ""} ${i < arr.length - 2 ? "border-b border-white/15 md:border-b-0" : ""}`}
              data-reveal data-reveal-delay={String((i % 3) + 1)}
            >
              <div className="flex items-baseline gap-2 justify-center md:justify-start">
                <div className="font-sans font-semibold leading-none tracking-tight text-[clamp(2.5rem,5.5vw,4.5rem)]">{it.value}</div>
                {it.unit && (
                  <div className="font-mono text-sm md:text-lg font-semibold" style={{ color: "var(--accent)" }}>
                    {it.unit}
                  </div>
                )}
              </div>
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-80 mt-3">{it.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ServicesGrid({ sec, media = {} }) {
  const { lang } = useSite();
  const c = pick(sec, lang);
  return (
    <section className="brut-section bg-[var(--bg)]" data-testid={`section-services-${sec.id}`}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 mb-12">
          <div className="col-span-12 md:col-span-3">{c.eyebrow && <div className="brut-tag" style={eyebrowStyle(c.eyebrow)}>{c.eyebrow}</div>}</div>
          <div className="col-span-12 md:col-span-9"><h2 className="brut-h2">{c.headline}</h2></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-[var(--ink)]">
          {(c.items || []).map((it, i) => {
            const m = it.image_media_id ? media?.[it.image_media_id] : null;
            const imgSrc = m ? ASSET_URL(m.variants?.webp || m.variants?.original || "") : null;
            return (
              <Link key={i} to={it.url || `/praxis-check?produkt=${encodeURIComponent(it.title || "")}`} className="flex flex-col border-b-2 lg:border-r-2 md:border-r-2 last:border-b-0 border-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors group lift-on-hover" data-testid={`service-card-${i}`} data-reveal data-reveal-delay={String((i % 3) + 1)}>
                <div className="aspect-[16/10] w-full overflow-hidden border-b-2 border-[var(--ink)] bg-[var(--line-soft)]">
                  {imgSrc ? (
                    <img src={imgSrc} alt={(lang === "en" ? m.alt_en : m.alt_de) || it.title} className={`w-full h-full ${sec?.data?.image_fit === "contain" ? "object-contain p-8 md:p-10" : "object-cover"} ${it.image_invert ? "brightness-0" : ""} ${sec.media_bw === false ? "" : "brut-bw"} group-hover:scale-[1.04] transition-transform duration-500`} loading="lazy" data-testid={`service-card-img-${i}`} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center opacity-25">
                      <ImageIcon size={40} strokeWidth={1.5} />
                    </div>
                  )}
                </div>
                <div className="p-8 md:p-10 flex-1">
                  <div className="flex items-start justify-between mb-6">
                    <span className="font-mono text-xs tracking-[0.2em] uppercase opacity-60">0{i + 1}</span>
                    <ArrowUpRight className="opacity-50 group-hover:opacity-100" />
                  </div>
                  <h3 className="brut-h3 mb-3">{it.title}</h3>
                  <p className="brut-body opacity-90">{it.text}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function PricingTable({ sec }) {
  const { lang } = useSite();
  const c = pick(sec, lang);
  return (
    <section id="preise" className="brut-section bg-[var(--bg)]" data-testid={`section-pricing-${sec.id}`}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 mb-12">
          <div className="col-span-12 md:col-span-3">{c.eyebrow && <div className="brut-tag" style={eyebrowStyle(c.eyebrow)}>{c.eyebrow}</div>}</div>
          <div className="col-span-12 md:col-span-9">
            <h2 className="brut-h2">{c.headline}</h2>
            {c.note && <p className="brut-body mt-4 max-w-2xl">{c.note}</p>}
          </div>
        </div>
        <div className="border-2 border-[var(--ink)]">
          {(c.rows || []).map((r, i) => (
            <div key={i} className={`flex items-baseline justify-between gap-6 px-6 md:px-10 py-6 ${i !== (c.rows||[]).length - 1 ? "border-b-2 border-[var(--ink)]" : ""}`} data-reveal data-reveal-delay={String((i % 3) + 1)}>
              <div className="brut-h3 font-sans !font-semibold">{r.label}</div>
              <div className="font-sans text-3xl md:text-4xl font-semibold tracking-tight whitespace-nowrap">{r.price}</div>
            </div>
          ))}
        </div>
        {c.cta_label && (
          <div className="mt-10"><Link to={c.cta_url || "#"} className="brut-btn">{c.cta_label} <ArrowUpRight size={16} /></Link></div>
        )}
      </div>
    </section>
  );
}

export function CtaBand({ sec }) {
  const { lang } = useSite();
  const c = pick(sec, lang);
  return (
    <section className="brut-section bg-[var(--accent)] text-[var(--ink)]" data-testid={`section-cta-${sec.id}`}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-24 grid grid-cols-12 gap-6 items-end">
        <div className="col-span-12 md:col-span-8" data-reveal>
          {c.eyebrow && <div className="brut-tag mb-6" style={eyebrowStyle(c.eyebrow)}>{c.eyebrow}</div>}
          <h2 className="brut-h2">{c.headline}</h2>
          {c.body && <p className="brut-body mt-4 max-w-2xl">{c.body}</p>}
        </div>
        <div className="col-span-12 md:col-span-4 flex md:justify-end" data-reveal data-reveal-delay="2">
          <Link to={c.cta_url || "/praxis-check"} className="brut-btn !bg-[var(--ink)] !text-[var(--bg)]" data-testid="cta-band-link">
            {c.cta_label} <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function Manifest({ sec }) {
  const { lang } = useSite();
  const c = pick(sec, lang);
  return (
    <section id="manifest" className="brut-section bg-[var(--bg)]" data-testid={`section-manifest-${sec.id}`}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 md:col-span-3">{c.eyebrow && <div className="brut-tag" style={eyebrowStyle(c.eyebrow)}>{c.eyebrow}</div>}</div>
          <div className="col-span-12 md:col-span-9"><h2 className="brut-h2">{c.headline}</h2></div>
        </div>
        <ul className="divide-y-2 divide-[var(--ink)] border-y-2 border-[var(--ink)]">
          {(c.items || []).map((it, i) => (
            <li key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6 py-8 md:py-10 group hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors px-4 md:px-2 text-center md:text-left" data-reveal data-reveal-delay={String((i % 3) + 1)}>
              <div className="md:col-span-2 font-sans text-4xl md:text-5xl font-semibold tracking-tight" style={{ color: "var(--accent)" }}>0{i+1}</div>
              <div className="md:col-span-4 brut-h3">{it.title}</div>
              <div className="md:col-span-6 brut-body">{it.text}</div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function FaqList({ sec }) {
  const { lang, t } = useSite();
  const c = pick(sec, lang);
  const [items, setItems] = React.useState([]);
  const [openId, setOpenId] = React.useState(null);
  const [filter, setFilter] = React.useState("Alle");
  const { api } = require("../../lib/api");
  React.useEffect(() => { api.faq().then(setItems); }, [api]);
  const categories = React.useMemo(() => ["Alle", ...Array.from(new Set(items.map(i => i.category)))], [items]);
  const filtered = items.filter(i => filter === "Alle" || i.category === filter);
  return (
    <section id={c.anchor || "faq"} className="brut-section bg-[var(--bg)]" data-testid={`section-faq-${sec.id}`}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 md:col-span-3">{c.eyebrow && <div className="brut-tag" style={eyebrowStyle(c.eyebrow)}>{c.eyebrow}</div>}</div>
          <div className="col-span-12 md:col-span-9"><h2 className="brut-h2">{c.headline}</h2></div>
        </div>
        <div className="flex flex-wrap gap-2 mb-8 justify-center md:justify-start">
          {categories.map(cat => (
            <button key={cat} onClick={() => setFilter(cat)} className={`font-mono text-[11px] tracking-[0.2em] uppercase px-3 py-2 border-2 border-[var(--ink)] ${filter===cat?"bg-[var(--ink)] text-[var(--bg)]":""}`} data-testid={`faq-filter-${cat}`}>{cat}</button>
          ))}
        </div>
        <ul className="border-y-2 border-[var(--ink)]">
          {filtered.map((it) => {
            const q = lang === "en" ? (it.question_en || it.question_de) : it.question_de;
            const a = lang === "en" ? (it.answer_en || it.answer_de) : it.answer_de;
            const isOpen = openId === it.id;
            return (
              <li key={it.id} className="border-b-2 border-[var(--ink)]">
                <button data-testid={`faq-trigger-${it.id}`} onClick={() => setOpenId(isOpen ? null : it.id)} className="w-full text-left py-6 md:py-7 flex items-start gap-6">
                  <span className="font-mono text-[11px] tracking-[0.2em] uppercase opacity-60 pt-1 hidden md:inline">{it.category}</span>
                  <span className="brut-h3 flex-1">{q}</span>
                  <span className="font-cond text-3xl font-black leading-none">{isOpen ? "−" : "+"}</span>
                </button>
                {isOpen && <div className="pb-8 pr-6 md:pl-[140px] brut-body whitespace-pre-line">{a}</div>}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

export function GlossaryList({ sec }) {
  const { lang } = useSite();
  const c = pick(sec, lang);
  const [items, setItems] = React.useState([]);
  const [letter, setLetter] = React.useState("Alle");
  const { api } = require("../../lib/api");
  React.useEffect(() => { api.glossary().then(setItems); }, [api]);
  const letters = React.useMemo(() => ["Alle", ...Array.from(new Set(items.map(i => i.letter))).sort()], [items]);
  const filtered = items.filter(i => letter === "Alle" || i.letter === letter);
  return (
    <section id={c.anchor || "glossar"} className="brut-section bg-[var(--bg)]" data-testid={`section-glossary-${sec.id}`}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 md:col-span-3">{c.eyebrow && <div className="brut-tag" style={eyebrowStyle(c.eyebrow)}>{c.eyebrow}</div>}</div>
          <div className="col-span-12 md:col-span-9"><h2 className="brut-h2">{c.headline}</h2></div>
        </div>
        <div className="flex flex-wrap gap-1 mb-8 justify-center md:justify-start">
          {letters.map(L => (
            <button key={L} onClick={() => setLetter(L)} className={`font-mono text-xs px-3 py-2 border-2 border-[var(--ink)] ${letter===L?"bg-[var(--ink)] text-[var(--bg)]":""}`} data-testid={`glossary-filter-${L}`}>{L}</button>
          ))}
        </div>
        <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-[var(--ink)]">
          {filtered.map((it, i) => (
            <div key={it.id} className={`p-6 ${i % 3 !== 2 ? "lg:border-r-2" : ""} ${i % 2 !== 1 ? "md:border-r-2 lg:border-r-2" : ""} border-b-2 border-[var(--ink)]`}>
              <dt className="brut-h3 mb-2">{lang === "en" ? (it.term_en || it.term_de) : it.term_de}</dt>
              <dd className="brut-body">{lang === "en" ? (it.definition_en || it.definition_de) : it.definition_de}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

export function NewsTeaser({ sec }) {
  const { lang, t } = useSite();
  const c = pick(sec, lang);
  const [items, setItems] = React.useState([]);
  const [media, setMedia] = React.useState([]);
  const sliderRef = React.useRef(null);
  const [canPrev, setCanPrev] = React.useState(false);
  const [canNext, setCanNext] = React.useState(false);

  const rawLimit = Number(sec?.data?.limit ?? sec?.limit ?? c?.limit ?? 50);
  const limit = Number.isFinite(rawLimit) && rawLimit > 0 ? Math.min(rawLimit, 100) : 50;

  const updateControls = React.useCallback(() => {
    const el = sliderRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 2);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  const scrollNews = React.useCallback((dir) => {
    const el = sliderRef.current;
    if (!el) return;
    const slide = el.querySelector(".news-teaser-slide");
    const amount = slide ? slide.getBoundingClientRect().width : el.clientWidth;
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
    window.setTimeout(updateControls, 320);
  }, [updateControls]);

  React.useEffect(() => {
    let cancelled = false;

    Promise.all([
      api.news().catch(() => []),
      api.media().catch(() => []),
    ]).then(([news, mediaAssets]) => {
      if (cancelled) return;
      setItems((news || []).filter(n => n.published !== false).slice(0, limit));
      setMedia(mediaAssets || []);
    });

    return () => { cancelled = true; };
  }, [limit]);

  React.useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;

    updateControls();
    el.addEventListener("scroll", updateControls, { passive: true });
    window.addEventListener("resize", updateControls);

    return () => {
      el.removeEventListener("scroll", updateControls);
      window.removeEventListener("resize", updateControls);
    };
  }, [items.length, updateControls]);

  const mediaMap = React.useMemo(
    () => Object.fromEntries((media || []).map(m => [m.id, m])),
    [media]
  );

  const imageFor = (n) => {
    const m = n.cover_media_id ? mediaMap[n.cover_media_id] : null;
    const path = m?.variants?.webp || m?.variants?.thumb || m?.variants?.original;
    return path ? ASSET_URL(path) : null;
  };

  if (!items.length) return null;

  return (
    <section id="aktuelles" className="brut-section bg-[var(--bg)]" data-testid={`section-news-${sec.id}`}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 mb-12 items-end">
          <div className="col-span-12 md:col-span-3">{c.eyebrow && <div className="brut-tag" style={eyebrowStyle(c.eyebrow)}>{c.eyebrow}</div>}</div>
          <div className="col-span-12 md:col-span-7"><h2 className="brut-h2">{c.headline}</h2></div>
          <div className="col-span-12 md:col-span-2 flex md:justify-end gap-3 news-teaser-controls">
            <button
              type="button"
              className="news-teaser-arrow"
              onClick={() => scrollNews(-1)}
              disabled={!canPrev}
              aria-label={t("Vorherige Beiträge", "Previous articles")}
            >
              ←
            </button>
            <button
              type="button"
              className="news-teaser-arrow"
              onClick={() => scrollNews(1)}
              disabled={!canNext}
              aria-label={t("Weitere Beiträge", "Next articles")}
            >
              →
            </button>
          </div>
        </div>

        <div className="news-teaser-shell bg-[var(--ink)] text-[var(--bg)] rounded-[28px] overflow-hidden shadow-xl relative">
          <button
            type="button"
            className="news-teaser-arrow news-teaser-arrow-left"
            onClick={() => scrollNews(-1)}
            disabled={!canPrev}
            aria-label={t("Vorherige Beiträge", "Previous articles")}
          >
            ←
          </button>
          <button
            type="button"
            className="news-teaser-arrow news-teaser-arrow-right"
            onClick={() => scrollNews(1)}
            disabled={!canNext}
            aria-label={t("Weitere Beiträge", "Next articles")}
          >
            →
          </button>
          <div ref={sliderRef} className="news-teaser-slider" aria-label={t("Aktuelle Fachbeiträge", "Latest articles")}>
            {items.map((n, i) => {
              const title = lang === "en" ? (n.title_en || n.title_de) : n.title_de;
              const excerpt = lang === "en" ? (n.excerpt_en || n.excerpt_de) : n.excerpt_de;
              const img = imageFor(n);

              return (
                <Link
                  to={`/wissen/news/${n.slug}`}
                  key={n.id}
                  className="news-card news-teaser-slide group block transition-colors hover:bg-[var(--ink)] hover:text-[var(--bg)]"
                  data-testid={`news-card-${i}`}
                >
                  {img && (
                    <div className="news-card-image">
                      <img src={img} alt={title} loading="lazy" />
                    </div>
                  )}

                  <div className="news-teaser-card-body p-7 md:p-8">
                    <div className="font-mono text-[11px] tracking-[0.2em] uppercase opacity-60 mb-5">{(n.published_at || "").slice(0, 10)}</div>
                    <h3 className="text-2xl md:text-3xl font-black leading-[0.98] tracking-[-0.04em] mb-4">{title}</h3>
                    {excerpt && <p className="news-teaser-excerpt brut-body opacity-90">{excerpt}</p>}
                    <span className="inline-flex items-center gap-1 mt-5 font-mono text-[11px] uppercase tracking-wider">{t("Weiterlesen", "Read more")} <ArrowUpRight size={14} /></span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// Parallax image section with adjustable height & parallax factor
export function ParallaxImage({ sec, media }) {
  const { lang } = useSite();
  const c = pick(sec, lang);
  const m = (lang === "en" && sec.media_id_en) ? media?.[sec.media_id_en] : media?.[sec.media_id || sec.media_id_en];
  const factor = typeof sec.parallax_factor === "number" ? sec.parallax_factor : 0.25;
  const heightVh = typeof sec.height_vh === "number" ? sec.height_vh : 70;
  const imgRef = React.useRef(null);
  useParallax(imgRef, -factor);
  if (!m) return null;
  return (
    <section className="brut-section relative overflow-hidden" data-testid={`section-parallax-${sec.id}`} style={{ height: `${heightVh}vh`, minHeight: 360 }}>
      <div ref={imgRef} className="absolute inset-0 will-change-transform" style={{ top: `${-factor * 260}px`, bottom: `${-factor * 260}px` }}>
        <img src={ASSET_URL(m.variants?.webp || m.variants?.original || "")} alt={lang === "en" ? m.alt_en : m.alt_de} className={`w-full h-full object-cover ${sec.media_bw === false ? "" : "brut-bw"}`} />
      </div>
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(236,235,229,0.0) 0%, rgba(236,235,229,0.0) 60%, rgba(236,235,229,0.92) 100%)" }} />
      <div className="relative max-w-[1440px] mx-auto px-5 md:px-10 h-full flex flex-col justify-end pb-12 md:pb-20" data-reveal="blur">
        {c.eyebrow && <div className="brut-tag mb-4 self-start glass-card-strong" style={eyebrowStyle(c.eyebrow)}>{c.eyebrow}</div>}
        {c.headline && <h2 className="brut-h2 max-w-2xl">{c.headline}</h2>}
        {c.body && <p className="brut-body max-w-xl mt-3">{c.body}</p>}
      </div>
    </section>
  );
}

// Brand decoration as its own section — fully manageable in CMS (delete, move, hide, swap media)
export function BrandDecoration({ sec, media }) {
  const { lang } = useSite();
  const c = pick(sec, lang);
  const ref = React.useRef(null);
  const factor = typeof sec.parallax_factor === "number" ? sec.parallax_factor : 0.25;
  useParallax(ref, -factor);
  const m = sec.media_id ? media?.[sec.media_id] : null;
  const src = m ? ASSET_URL(m.variants?.original || m.variants?.webp || "") : "/brand/circle.svg";
  const sizeVw = c.size_vw || 38;
  const pos = c.position || "right"; // right | left | center
  const heightVh = c.height_vh || 60;
  const opacity = typeof c.opacity === "number" ? c.opacity : 1;
  return (
    <section className="relative overflow-visible" style={{ height: `${heightVh}vh`, minHeight: 300 }} data-testid={`section-brand-decoration-${sec.id}`}>
      <div
        ref={ref}
        className="absolute brand-circle pointer-events-none"
        style={{
          [pos === "center" ? "left" : pos]: pos === "center" ? "50%" : "-6%",
          transform: pos === "center" ? "translateX(-50%)" : undefined,
          top: "10%",
          width: `min(640px, ${sizeVw}vw)`,
          opacity,
        }}
      >
        <img src={src} alt={c.alt || ""} aria-hidden={!c.alt} className="w-full h-auto" />
      </div>
    </section>
  );
}

// Full-bleed media banner: image / video / html_animation across full width,
// with optional H2 headline (alignable left/center/right) and opacity control.
export function MediaBanner({ sec, media }) {
  const { lang } = useSite();
  const c = pick(sec, lang);
  const effMediaId = (lang === "en" && sec.media_id_en) ? sec.media_id_en : (sec.media_id || sec.media_id_en);
  const effMediaMode = (lang === "en" && sec.media_mode_en) ? sec.media_mode_en : (sec.media_mode || sec.media_mode_en);
  const m = media?.[effMediaId];
  const mediaMode = effMediaMode || (m ? m.media_type : null);
  const heightVh = typeof sec.height_vh === "number" ? sec.height_vh : 70;
  const opacityPct = (typeof sec.media_opacity === "number")
    ? sec.media_opacity
    : (mediaMode === "html_animation" ? 70 : 35);
  const mediaOpacity = Math.max(0, Math.min(100, opacityPct)) / 100;
  const align = c.align || "center"; // left | center | right
  const alignClass = align === "left" ? "items-start text-left" : align === "right" ? "items-end text-right" : "items-center text-center";
  const hasText = !!(c.eyebrow || c.headline || c.body || c.cta_label);
  return (
    <section
      className="relative w-full overflow-hidden bg-[var(--ink)]"
      style={{ height: `${heightVh}vh`, minHeight: 320 }}
      data-testid={`section-media-banner-${sec.id}`}
    >
      {m && mediaMode === "image" && (
        <img src={ASSET_URL(m.variants?.webp || m.variants?.original || "")} alt={lang === "en" ? m.alt_en || "" : m.alt_de || ""} className="absolute inset-0 w-full h-full object-cover" style={{ opacity: mediaOpacity }} loading="lazy" />
      )}
      {m && mediaMode === "video" && (
        <video className="bg-video absolute inset-0 w-full h-full object-cover" style={{ opacity: mediaOpacity }} src={ASSET_URL(m.variants?.original || "")} autoPlay muted loop playsInline preload="metadata" />
      )}
      {m && mediaMode === "html_animation" && (
        <iframe title={m.title || "Animation"} src={ASSET_URL(m.variants?.original || "")} className="absolute inset-0 w-full h-full pointer-events-none border-0" style={{ opacity: mediaOpacity }} />
      )}
      {hasText && (
        <div className={`relative h-full max-w-[1440px] mx-auto px-5 md:px-10 flex flex-col justify-center ${alignClass}`} data-reveal>
          <div className="max-w-3xl">
            {c.eyebrow && <div className="brut-tag mb-5 glass-card-strong" style={eyebrowStyle(c.eyebrow)}>{c.eyebrow}</div>}
            {c.headline && <h2 className="brut-h2 text-white" style={{ textShadow: "0 2px 24px rgba(0,0,0,0.45)" }}>{(c.headline || "").split("\n").map((l, i) => <span key={i} className="block">{l}</span>)}</h2>}
            {c.body && <p className="brut-body text-white/90 mt-4" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.4)" }}>{c.body}</p>}
            {c.cta_label && (
              <div className="mt-8">
                <Link to={c.cta_url || "#"} className="brut-btn" data-testid="media-banner-cta">{c.cta_label} <ArrowUpRight size={16} /></Link>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// Editorial click-to-play video with optional poster, introduction and transcript.
// Unlike background videos, it never autoplays and only downloads metadata initially.
export function VideoPlayer({ sec, media }) {
  const { lang, t } = useSite();
  const c = pick(sec, lang);
  const video = media?.[sec.media_id];
  const poster = media?.[sec.poster_media_id];
  const videoSrc = video ? ASSET_URL(video.variants?.original || "") : "";
  const posterSrc = poster ? ASSET_URL(poster.variants?.webp || poster.variants?.original || "") : "";
  const videoRef = React.useRef(null);
  const [started, setStarted] = React.useState(false);
  const aspectClass = c.aspect === "portrait"
    ? "aspect-[9/16] max-w-xl mx-auto"
    : c.aspect === "square"
      ? "aspect-square max-w-3xl mx-auto"
      : "aspect-video";

  const startVideo = () => {
    setStarted(true);
    window.requestAnimationFrame(() => videoRef.current?.play().catch(() => {}));
  };

  return (
    <section className="py-16 md:py-24 bg-[var(--bg)]" data-testid={`section-video-player-${sec.id}`}>
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        {(c.eyebrow || c.headline || c.body) && (
          <div className="max-w-3xl mb-8 md:mb-10" data-reveal>
            {c.eyebrow && <div className="brut-tag mb-5">{c.eyebrow}</div>}
            {c.headline && <h2 className={headlineClass(c.headline)}>{c.headline}</h2>}
            {c.body && <p className="brut-body mt-4 whitespace-pre-line">{c.body}</p>}
          </div>
        )}

        <div className={`relative overflow-hidden rounded-[24px] bg-black ${aspectClass}`} data-reveal>
          {videoSrc ? (
            <>
              <video
                ref={videoRef}
                src={videoSrc}
                poster={posterSrc || undefined}
                controls={started}
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-contain bg-black"
                aria-label={c.video_title || c.headline || video.title || t("Video", "Video")}
                onPlay={() => setStarted(true)}
              />
              {!started && (
                <button
                  type="button"
                  onClick={startVideo}
                  className="absolute inset-0 z-10 flex items-center justify-center bg-black/15 hover:bg-black/25 transition-colors group"
                  aria-label={t("Video abspielen", "Play video")}
                  data-testid="video-player-play"
                >
                  <span className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[var(--accent)] text-[var(--ink)] flex items-center justify-center shadow-2xl group-hover:scale-105 transition-transform">
                    <Play size={38} fill="currentColor" aria-hidden="true" />
                  </span>
                </button>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/70 font-mono text-sm">
              {t("Bitte Video im CMS auswählen", "Please select a video in the CMS")}
            </div>
          )}
        </div>

        {c.caption && <p className="mt-3 text-sm opacity-70">{c.caption}</p>}
        {c.transcript && (
          <details className="mt-6 border-t-2 border-[var(--ink)] pt-4">
            <summary className="font-bold cursor-pointer">{t("Videotranskript anzeigen", "Show video transcript")}</summary>
            <div className="brut-body mt-4 whitespace-pre-line max-w-4xl">{c.transcript}</div>
          </details>
        )}
      </div>
    </section>
  );
}

// Press / media coverage list — pulls items from /api/public/press.
// Supports external links AND uploaded PDFs (preview image + in-page viewer).
export function PressList({ sec, media = {} }) {
  const { lang, t } = useSite();
  const c = pick(sec, lang);
  const [items, setItems] = React.useState([]);
  const [viewing, setViewing] = React.useState(null); // {src, title}

  React.useEffect(() => {
    api.press().then(setItems).catch(() => setItems([]));
  }, []);

  const fmtDate = (d) => {
    if (!d) return "";
    const date = new Date(d);
    if (isNaN(date)) return d;
    return date.toLocaleDateString(lang === "en" ? "en-GB" : "de-DE", { year: "numeric", month: "long", day: "numeric" });
  };

  return (
    <section className="brut-section bg-[var(--bg)]" data-testid={`section-press-${sec.id}`}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-3">{c.eyebrow && <div className="brut-tag" style={eyebrowStyle(c.eyebrow)}>{c.eyebrow}</div>}</div>
          <div className="col-span-12 md:col-span-9">
            <h2 className="brut-h2">{c.headline || t("Presse", "Press")}</h2>
            {c.body && <p className="brut-body mt-4 max-w-2xl">{c.body}</p>}
          </div>
        </div>

        {items.length === 0 ? (
          <p className="brut-body opacity-60">{t("Aktuell keine Presseberichte.", "No press articles yet.")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((it, i) => {
              const title = lang === "en" ? (it.title_en || it.title_de) : it.title_de;
              const summary = lang === "en" ? (it.summary_en || it.summary_de) : it.summary_de;
              const pdf = it.pdf_media_id ? media?.[it.pdf_media_id] : null;
              const imgAsset = it.image_media_id ? media?.[it.image_media_id] : null;
              const pdfUrl = pdf ? (pdf.variants?.original || "") : null;
              // Preview image: explicit image first, else PDF page-1 preview
              const previewUrl = imgAsset
                ? ASSET_URL(imgAsset.variants?.webp || imgAsset.variants?.thumb || imgAsset.variants?.original || "")
                : pdf ? ASSET_URL(pdf.variants?.preview || pdf.variants?.thumb || "") : null;
              const openPreview = () => {
                if (pdfUrl) setViewing({ src: pdfUrl, title });
                else if (it.url) window.open(it.url, "_blank", "noopener,noreferrer");
              };
              return (
                <article key={it.id || i} className="border-2 border-[var(--ink)] p-6 flex flex-col gap-4" data-testid={`press-card-${i}`} data-reveal data-reveal-delay={String((i % 3) + 1)}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-60">{it.medium}</span>
                    <span className="font-mono text-[10px] opacity-50">{fmtDate(it.date)}</span>
                  </div>
                  <h3 className="brut-h3 leading-tight">{title}</h3>
                  {summary && <p className="brut-body text-sm opacity-80">{summary}</p>}

                  {previewUrl && (
                    <button onClick={openPreview} className="block w-full overflow-hidden border border-[var(--line-soft)] bg-white aspect-[16/10]" data-testid={`press-preview-${i}`} aria-label={`${title} – ${pdfUrl ? "PDF ansehen" : "Artikel öffnen"}`}>
                      <img src={previewUrl} alt={`${title} – Vorschau`} className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  )}

                  <div className="mt-auto flex flex-wrap items-center gap-4 pt-2">
                    {pdfUrl && (
                      <button onClick={() => setViewing({ src: pdfUrl, title })} className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.14em] uppercase underline" data-testid={`press-view-pdf-${i}`}>
                        <Eye size={13} /> {t("Ansehen", "View")}
                      </button>
                    )}
                    {it.url && (
                      <a href={it.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.14em] uppercase underline" data-testid={`press-source-${i}`}>
                        <ExternalLink size={13} /> {t("Quelle", "Source")}
                      </a>
                    )}
                    {pdfUrl && !previewUrl && <FileText size={14} className="opacity-40" />}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
      {viewing && <PdfViewer src={viewing.src} title={viewing.title} onClose={() => setViewing(null)} />}
    </section>
  );
}

// Renderer dispatcher
const REG = { hero: Hero, text: TextBlock, text_image: TextImage, workflow_steps: WorkflowSteps, stats: Stats, services_grid: ServicesGrid, pricing_table: PricingTable, cta_band: CtaBand, faq_list: FaqList, glossary_list: GlossaryList, news_teaser: NewsTeaser, manifest: Manifest, parallax_image: ParallaxImage, brand_decoration: BrandDecoration, media_banner: MediaBanner, video_player: VideoPlayer, press_list: PressList };

export default function SectionRenderer({ sections = [], media = {} }) {
  // Reveal on scroll — also watches for async-loaded content (e.g. the press
  // grid, news, glossary) so late-mounted [data-reveal] cards don't stay hidden.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (document.body.classList.contains("a11y-no-motion")) {
      const revealAll = () => document.querySelectorAll("[data-reveal]").forEach((el) => el.classList.add("is-visible"));
      revealAll();
      const mo = new MutationObserver(revealAll);
      mo.observe(document.body, { childList: true, subtree: true });
      return () => mo.disconnect();
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("is-visible"); io.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: "0px 0px -10% 0px" }
    );
    const observeNew = () => document.querySelectorAll("[data-reveal]:not(.is-visible)").forEach((el) => io.observe(el));
    observeNew();
    let raf = 0;
    const mo = new MutationObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(observeNew); });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); cancelAnimationFrame(raf); };
  }, []);

  return (
    <>
      {(sections || []).filter(s => s.enabled !== false).sort((a, b) => (a.order||0) - (b.order||0)).map((sec) => {
        const Comp = REG[sec.type];
        if (!Comp) return null;
        return <Comp key={sec.id} sec={sec} media={media} />;
      })}
    </>
  );
}
